import { useParams, useNavigate } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import { useSEO } from '@/hooks/useSEO';
import { communities } from '@/mocks/communities';
import ScrollBatteryBar from '@/components/feature/ScrollBatteryBar';
import SiteHeader from '@/components/feature/SiteHeader';
import Footer from '@/pages/home/components/Footer';

const rankingTypes = [
  { key: 'monthly', label: 'Top By Monthly', icon: 'ri-calendar-line', desc: 'Most active communities this month by message volume' },
  { key: 'categories', label: 'Top By Categories', icon: 'ri-apps-2-line', desc: 'Best-rated community in each category' },
  { key: 'regions', label: 'Top By Regions', icon: 'ri-map-pin-line', desc: 'Top communities grouped by geographic region' },
  { key: 'source', label: 'Top By Source', icon: 'ri-share-forward-line', desc: 'Communities with the highest referral traffic' },
  { key: 'revenue', label: 'Top By Revenue', icon: 'ri-money-dollar-circle-line', desc: 'Highest earning monetized communities' },
];

const categoryColors: Record<string, string> = {
  IT: '#0088CC', Finance: '#16a34a', Games: '#7c3aed',
  Health: '#e11d48', Arts: '#d97706', Education: '#0891b2', Business: '#64748b',
};

// Mini sparkline bar chart component
function SparkBar({ value, max, color = '#0088CC' }: { value: number; max: number; color?: string }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(max > 0 ? (value / max) * 100 : 0), 80);
    return () => clearTimeout(t);
  }, [value, max]);

  return (
    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700 ease-out"
        style={{ width: `${width}%`, background: color }}
      />
    </div>
  );
}

// Animated counter
function AnimatedNumber({ value, suffix = '' }: { value: number; suffix?: string }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = value / 40;
    const t = setInterval(() => {
      start += step;
      if (start >= value) { setDisplay(value); clearInterval(t); }
      else setDisplay(Math.floor(start));
    }, 20);
    return () => clearInterval(t);
  }, [value]);
  return <>{display.toLocaleString()}{suffix}</>;
}

export default function RankingPage() {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, [type]);

  const current = rankingTypes.find((r) => r.key === type) ?? rankingTypes[0];

  useSEO({
    title: `${current.label} – Telegram Community Rankings | TGDirectory`,
    description: `${current.desc}. See the top-ranked Telegram communities on TGDirectory.`,
    keywords: `Telegram community ranking, ${current.label.toLowerCase()}, top Telegram groups, TGDirectory`,
    canonical: `https://tgdirectory.app/ranking/${type ?? 'monthly'}`,
  });

  const rankedList = useMemo(() => {
    const list = [...communities];
    if (type === 'monthly') return list.sort((a, b) => (b.weeklyMessages ?? 0) - (a.weeklyMessages ?? 0));
    if (type === 'categories') {
      const byCategory: Record<string, typeof communities[0]> = {};
      list.forEach((c) => {
        if (!byCategory[c.category] || c.memberCount > byCategory[c.category].memberCount) byCategory[c.category] = c;
      });
      return Object.values(byCategory).sort((a, b) => b.memberCount - a.memberCount);
    }
    if (type === 'regions') {
      const byRegion: Record<string, typeof communities[0]> = {};
      list.forEach((c) => {
        if (!byRegion[c.region] || c.memberCount > byRegion[c.region].memberCount) byRegion[c.region] = c;
      });
      return Object.values(byRegion).sort((a, b) => b.memberCount - a.memberCount);
    }
    if (type === 'source') return list.sort((a, b) => ((b.adminCount ?? 0) * (b.weeklyMessages ?? 0)) - ((a.adminCount ?? 0) * (a.weeklyMessages ?? 0)));
    if (type === 'revenue') return list.sort((a, b) => b.memberCount - a.memberCount);
    return list.sort((a, b) => b.memberCount - a.memberCount);
  }, [type]);

  const getMedal = (i: number) => {
    if (i === 0) return { color: '#F59E0B', label: '🥇', bg: 'from-amber-50 to-amber-100/50', border: 'border-amber-200/60' };
    if (i === 1) return { color: '#94A3B8', label: '🥈', bg: 'from-slate-50 to-slate-100/50', border: 'border-slate-200/60' };
    if (i === 2) return { color: '#B45309', label: '🥉', bg: 'from-orange-50 to-orange-100/50', border: 'border-orange-200/60' };
    return null;
  };

  const getMetricValue = (c: typeof communities[0]) => {
    if (type === 'monthly') return (c.weeklyMessages ?? 0) * 4;
    if (type === 'source') return (c.adminCount ?? 0) * (c.weeklyMessages ?? 0);
    if (type === 'revenue') return Math.round(c.memberCount * 0.02);
    return c.memberCount;
  };

  const getMetricLabel = (c: typeof communities[0]) => {
    if (type === 'monthly') return `${getMetricValue(c).toLocaleString()} msgs/mo`;
    if (type === 'source') return `${getMetricValue(c).toLocaleString()} score`;
    if (type === 'revenue') return `~$${getMetricValue(c).toLocaleString()}/mo`;
    return `${c.memberCount.toLocaleString()} members`;
  };

  const maxMetric = Math.max(...rankedList.map(getMetricValue), 1);

  const totalMembers = communities.reduce((s, c) => s + c.memberCount, 0);
  const totalMsgs = communities.reduce((s, c) => s + (c.weeklyMessages ?? 0), 0);

  // Distribution chart data (top categories by member count)
  const catData = useMemo(() => {
    const map: Record<string, number> = {};
    communities.forEach((c) => { map[c.category] = (map[c.category] ?? 0) + c.memberCount; });
    const total = Object.values(map).reduce((a, b) => a + b, 0);
    return Object.entries(map)
      .map(([cat, cnt]) => ({ cat, cnt, pct: Math.round((cnt / total) * 100) }))
      .sort((a, b) => b.cnt - a.cnt)
      .slice(0, 5);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f8fb] via-[#f8fafc] to-[#f3f7fa] text-slate-800 font-sans">
      <ScrollBatteryBar />
      <SiteHeader showBack />

      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-slate-400 mb-5">
          <button onClick={() => navigate('/')} className="hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
          <span>/</span>
          <span className="text-[#0088CC] font-medium">{current.label}</span>
        </nav>

        <div className="flex gap-6 items-start">
          {/* Main */}
          <div
            className="flex-1 min-w-0 transition-all duration-500"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)' }}
          >
            {/* Header */}
            <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-white/80 px-6 py-5 mb-5">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#0088CC]/10 border border-[#0088CC]/20 shrink-0">
                  <i className={`${current.icon} text-xl text-[#0088CC]`}></i>
                </span>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-extrabold text-slate-800">{current.label}</h1>
                  <p className="text-xs text-slate-400">{current.desc}</p>
                </div>
                <div className="hidden md:flex items-center gap-5 shrink-0">
                  {[
                    { icon: 'ri-community-line', label: 'Communities', value: rankedList.length },
                    { icon: 'ri-group-line', label: 'Total Members', value: rankedList.reduce((s, c) => s + c.memberCount, 0) },
                  ].map((s) => (
                    <div key={s.label} className="text-center">
                      <p className="text-lg font-extrabold text-slate-800">
                        <AnimatedNumber value={s.value} />
                      </p>
                      <p className="text-[10px] text-slate-400">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Top 3 Podium */}
            {rankedList.length >= 3 && (
              <div className="grid grid-cols-3 gap-3 mb-5">
                {([rankedList[1], rankedList[0], rankedList[2]] as typeof rankedList).map((c, podiumIdx) => {
                  const rank = podiumIdx === 0 ? 1 : podiumIdx === 1 ? 0 : 2;
                  const medal = getMedal(rank)!;
                  const heights = ['h-36', 'h-44', 'h-32'];
                  const delays = ['delay-100', 'delay-0', 'delay-200'];
                  return (
                    <div
                      key={c.id}
                      onClick={() => navigate(`/community/${c.id}`)}
                      className={`relative bg-gradient-to-b ${medal.bg} backdrop-blur-md rounded-2xl border ${medal.border} p-4 flex flex-col items-center justify-end cursor-pointer hover:scale-[1.02] transition-all duration-300 ${heights[podiumIdx]} ${delays[podiumIdx]}`}
                      style={{
                        opacity: visible ? 1 : 0,
                        transform: visible ? 'translateY(0)' : 'translateY(20px)',
                        transition: `opacity 0.5s ${podiumIdx * 0.1}s, transform 0.5s ${podiumIdx * 0.1}s`,
                      }}
                    >
                      <div className="absolute top-3 left-3 text-xl">{medal.label}</div>
                      <img src={c.avatar} alt={c.name} className="w-12 h-12 rounded-xl object-cover object-top mb-2 border-2 border-white" />
                      <p className="text-xs font-bold text-slate-700 text-center line-clamp-2 leading-tight">{c.name}</p>
                      <p className="text-[10px] text-[#0088CC] font-semibold mt-1">{getMetricLabel(c)}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Full List */}
            <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-white/80 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100/80 flex items-center justify-between">
                <span className="text-sm font-bold text-slate-700">Full Rankings</span>
                <span className="text-xs text-slate-400">{rankedList.length} communities</span>
              </div>
              <div className="divide-y divide-slate-100/60">
                {rankedList.map((c, i) => {
                  const medal = getMedal(i);
                  const tagColor = categoryColors[c.category] ?? '#0088CC';
                  const metricVal = getMetricValue(c);
                  return (
                    <div
                      key={c.id}
                      onClick={() => navigate(`/community/${c.id}`)}
                      className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/50 transition-colors cursor-pointer group"
                      style={{
                        opacity: visible ? 1 : 0,
                        transform: visible ? 'translateX(0)' : 'translateX(-12px)',
                        transition: `opacity 0.4s ${Math.min(i * 0.04, 0.6)}s, transform 0.4s ${Math.min(i * 0.04, 0.6)}s`,
                      }}
                    >
                      {/* Rank */}
                      <div className="w-8 shrink-0 text-center">
                        {medal ? (
                          <span className="text-base">{medal.label}</span>
                        ) : (
                          <span className="text-sm font-semibold text-slate-400">{i + 1}</span>
                        )}
                      </div>
                      {/* Avatar */}
                      <img src={c.avatar} alt={c.name} className="w-10 h-10 rounded-xl object-cover object-top shrink-0 border border-white/80" />
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-bold text-slate-800 group-hover:text-[#0088CC] transition-colors truncate">{c.name}</span>
                          {c.featured && (
                            <span className="text-[9px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200/60 shrink-0">
                              Featured
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: `${tagColor}15`, color: tagColor }}>
                            {c.category}
                          </span>
                          <span className="text-[10px] text-slate-400">{c.region}</span>
                        </div>
                        {/* Progress bar */}
                        <div className="flex items-center gap-2">
                          <SparkBar value={metricVal} max={maxMetric} color={tagColor} />
                          <span className="text-[9px] text-slate-400 shrink-0">{Math.round((metricVal / maxMetric) * 100)}%</span>
                        </div>
                      </div>
                      {/* Metric */}
                      <div className="shrink-0 text-right ml-2">
                        <p className="text-sm font-bold text-[#0088CC]">{getMetricLabel(c)}</p>
                        <p className="text-[10px] text-slate-400">{c.activityLevel} activity</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="hidden xl:flex flex-col w-64 shrink-0 sticky top-20 gap-4">
            {/* Ranking Types */}
            <div className="bg-[#ddeaf5]/75 backdrop-blur-xl rounded-2xl border border-[#b8d4ea]/60 p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center text-[#0088CC]">
                  <i className="ri-trophy-line text-sm"></i>
                </span>
                Ranking Types
              </h3>
              <div className="flex flex-col gap-1">
                {rankingTypes.map((r) => {
                  const isActive = r.key === (type ?? 'monthly');
                  return (
                    <button
                      key={r.key}
                      onClick={() => navigate(`/ranking/${r.key}`)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all cursor-pointer text-left ${
                        isActive
                          ? 'bg-[#0088CC] border-[#0088CC] text-white'
                          : 'bg-white/50 border-[#c5d9eb]/50 text-slate-600 hover:border-[#0088CC]/30 hover:bg-white/70'
                      }`}
                    >
                      <span className={`w-6 h-6 flex items-center justify-center rounded-lg shrink-0 ${isActive ? 'bg-white/20' : 'bg-[#0088CC]/10'}`}>
                        <i className={`${r.icon} text-xs ${isActive ? 'text-white' : 'text-[#0088CC]'}`}></i>
                      </span>
                      <span className="text-xs font-medium">{r.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Stats card */}
            <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white/80 p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3">Platform Stats</h3>
              <div className="flex flex-col gap-2.5">
                {[
                  { label: 'Total Communities', value: communities.length, icon: 'ri-community-line', suffix: '' },
                  { label: 'Total Members', value: Math.round(totalMembers / 1000), icon: 'ri-group-line', suffix: 'k+' },
                  { label: 'Weekly Messages', value: Math.round(totalMsgs / 1000), icon: 'ri-chat-3-line', suffix: 'k' },
                  { label: 'Active Categories', value: 7, icon: 'ri-apps-2-line', suffix: '' },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center gap-2.5">
                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#0088CC]/10 shrink-0">
                      <i className={`${stat.icon} text-xs text-[#0088CC]`}></i>
                    </span>
                    <div>
                      <p className="text-xs font-bold text-slate-700">
                        <AnimatedNumber value={stat.value} suffix={stat.suffix} />
                      </p>
                      <p className="text-[10px] text-slate-400">{stat.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Category distribution chart */}
            <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white/80 p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3">Member Distribution</h3>
              <div className="flex flex-col gap-2.5">
                {catData.map((d) => {
                  const color = categoryColors[d.cat] ?? '#0088CC';
                  return (
                    <div key={d.cat}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-medium text-slate-600">{d.cat}</span>
                        <span className="text-[10px] text-slate-400">{d.pct}%</span>
                      </div>
                      <SparkBar value={d.pct} max={100} color={color} />
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
